import { Navigate, useParams, useLocation } from "react-router-dom";
import { trackRedirect } from "@/hooks/useSeoTracking";

/**
 * Slug-to-slug overrides extracted from vercel.json.
 * Key = old slug (without prefix), Value = new slug under /nos-formations/
 */
const SLUG_OVERRIDES: Record<string, string> = {
  // /course/* overrides (ancien site)
  "developper-sa-posture-de-manager": "se-positionner-en-tant-que-leader",
  "elaborer-son-plan-strategique-et-commercial-3": "elaborer-son-plan-strategique-et-commercial",
  "formation-de-formateur": "formation-de-formateur-interne",
  "gestion-de-projet-les-fondamentaux": "green-it-les-fondamentaux",
  "le-management-transversal-non-hierarchique-2": "le-management-transversal-non-hierarchique",
  "pratiquer-lecoute-active-5": "pratiquer-l-ecoute-active",
  "pratiquer-lecoute-active-8": "pratiquer-l-ecoute-active",
  "pratiquer-lecoute-active-9": "pratiquer-l-ecoute-active",
  "prevenir-les-risques-psychosociaux-2": "prevenir-les-risques-psychosociaux",
  "recruter-et-integrer-un-nouveau-collaborateur-4": "recruter-et-integrer-un-nouveau-collaborateur",
  "sst-formation-initiale": "formation-initiale-sauveteur-secouriste-du-travail",
  // Ancien site → cibles mises à jour (avril 2026)
  "management-dequipe-de-proximite-au-quotidien": "manager-au-quotidien",
  "la-commission-sante-securite-et-conditions-de-travail-cssctde-300-salaries": "formation-sante-securite-et-conditions-de-travail",
  "formation-des-membres-du-cse-11-a-49-salaries": "formation-des-membres-du-cseplus-de-50-salaries",
  // Redirections anciens slugs → nouveaux slugs (CSV avril 2026)
  "techniques-de-vente-et-de-negociation-commerciale": "techniques-de-vente-et-de-negociation",
  "accompagner-et-piloter-le-changement": "accompagner-ses-collaborateurs",
  "ameliorer-son-organisation-pour-gagner-en-efficacite": "parcours-gerer-son-temps-et-gagner-en-efficacite",
  "langues-etrangeres-en-cours-individuel": "/nos-formations?pole=langues",
  "animer-et-gerer-votre-equipe-au-quotidien": "manager-au-quotidien",
  "autorisation-interne-de-conduite-chariots-de-manutention": "conduite-en-securite-des-chariots-de-manutention-apprentissage-de-la-conduite-evaluation-prealable-a-lautorisation-de-conduite",
  "caces-r-484-ponts-roulants": "formation-preparatoire-et-examen-du-caces-r484",
  "caces-r-485-gerbeurs-a-conducteur-accompagnant": "formation-preparatoire-et-examen-du-caces-r485",
  "caces-r-486-plateformes-elevatrices": "conduite-en-securite-des-plateformes-elevatrice-apprentissage-de-la-conduite-evaluation-prealable-a-lautorisation-de-conduite",
  "caces-r-489-chariots-de-manutention": "formation-preparatoire-et-examen-du-caces-r489",
  "cse-formation-sante-securite-et-conditions-de-travail-premier-mandat": "formation-sante-securite-et-conditions-de-travail",
  "developper-son-leadership": "se-positionner-en-tant-que-leader",
  "excel-initiation": "excel",
  "exercer-une-mission-de-tuteur-ou-de-maitre-d-apprentissage": "devenir-tuteur-en-entreprise",
  
  "formation-des-membres-du-cse": "formation-des-membres-du-cseplus-de-50-salaries",
  "housekeeping-gouvernante-d-etage": "acquerir-les-bases-du-metier-de-femme-de-chambre",
  "incendie-equipier-d-evacuation-guide-file-serre-file": "equipier-devacuation-guide-file-serre-file",
  // (mapping supprimé : "integration-du-personnel-en-situation-de-handicap" est désormais l'URL réelle de RH-052)
  "les-bases-de-la-relation-commerciale-niveau-1": "professionnaliser-sa-demarche-commerciale",
  "maitriser-copilot-avec-microsoft-365": "ia-optimiser-son-quotidien-avec-microsoft-copilot",
  "mettre-en-uvre-un-plan-de-departs-volontaires-pdv": "plan-de-depart-volontaires-pdv-de-la-procedure-a-laccompagnement-des-salaries",
  "photoshop-initiation": "formation-infographie-photoshop-illustrator-indesign",
  "pratiquer-un-management-participatif": "parcours-developper-le-travail-collaboratif",
  "qualite-de-vie-et-bien-etre-au-travail": "la-qvt-qualite-de-vie-au-travail",
  "la-vente-par-les-reseaux-sociaux": "/nos-formations?pole=digital-ia-bureautique",
  // (suppression mapping erroné: recyclage-... pointait vers preparation-...)
  "accueillir-informer-et-orienter-les-personnes-en-situations-de-handicap-en-milieur-touristique": "referent-handicap",
  "referent-handicap-2": "referent-handicap",
  "devenir-referent-handicap-mission-instaure-pour-toutes-les-structures-de-250-salaries-par-la-lois-avenir-professionnel": "referent-handicap",
  "negocier-avec-les-fournisseurs": "/",
  "optimiser-son-management": "optimiser-le-management-de-ses-ressources",
  "referent-harcelement-sexuel-agissement-sexistes-apres-la-loi-avenir-professionnel-2": "referent-harcelement-sexuel-agissements-sexistes",
  "referent-harcelement-sexuel-et-agissements-sexistes": "referent-harcelement-sexuel-agissements-sexistes",
  "techniques-de-vente-negociation-commerciale-niveau-1": "techniques-de-vente-et-de-negociation",
  "transformer-sa-strategie-de-vente-grace-a-l-ia": "lia-pour-le-commercial-et-la-vente",
  "travailler-avec-des-fichiers-dans-chatgpt": "ia-chatgpt-niveau-2",
  "piloter-son-equipe": "/nos-formations?pole=management-rh",
  
  "saffirmer-pour-mieux-communiquer": "s-affirmer-pour-mieux-communiquer",
  // Extras 1-to-many : anciennes URLs → slug principal
  "manager-une-equipe-en-hotellerie-restauration": "manager-au-quotidien",
  "conduite-en-securite-des-transpalettes-et-gerbeurs-apprentissage-de-la-conduite-evaluation-prealable-a-lautorisation-de-conduite": "formation-preparatoire-et-examen-du-caces-r485",
  "formation-preparatoire-et-examen-du-caces-r482": "formation-preparatoire-et-examen-du-caces-r485",
  "formation-preparatoire-et-examen-du-caces-r486": "conduite-en-securite-des-plateformes-elevatrice-apprentissage-de-la-conduite-evaluation-prealable-a-lautorisation-de-conduite",
  "developper-une-politique-en-faveur-des-salaries-handicapes": "referent-handicap",
  "formation-de-formateur-a-lautorisation-interne-de-conduite": "formation-de-formateur-a-l-autorisation-interne-de-conduite",
  // Habilitation élec non-élec — refonte Pareo (étape 1A) : SEC-010, SEC-013-A → SEC-013-B0 (H0-B0) ; SEC-013-B → SEC-013-BS (BS-BE)
  "preparation-a-l-habilitation-electrique-personnel-non-electricien": "preparation-a-l-habilitation-electrique-personnel-non-electricien-h0-b0",
  "preparation-a-l-habilitation-electrique-personnel-non-electricien-h0-h0v": "preparation-a-l-habilitation-electrique-personnel-non-electricien-h0-b0",
  "preparation-a-l-habilitation-electrique-personnel-non-electricien-b0-h0-h0v": "preparation-a-l-habilitation-electrique-personnel-non-electricien-h0-b0",
  "preparation-a-l-habilitation-electrique-personnel-non-electricien-b0v": "preparation-a-l-habilitation-electrique-personnel-non-electricien-bs-be",
  "preparation-a-l-habilitation-electrique-personnel-non-electricien-bs-be-man-uvre": "preparation-a-l-habilitation-electrique-personnel-non-electricien-bs-be",
  // Recyclage non-électricien — refonte Pareo (étape 1B) : SEC-015 → SEC-014-B0 (H0-B0) ; nouvelle SEC-014-BS (BS-BE)
  "recyclage-habilitation-electrique-personnel-non-electricien": "recyclage-habilitation-electrique-personnel-non-electricien-h0-b0",
  // Anciens slugs sans cible directe → page catalogue filtrée pôle bureautique
  "formation-bureautique-word-excel-powerpoint": "/nos-formations?pole=digital-ia-bureautique",
  "pwer-b-i-concevoir-des-tableaux-de-bord": "/nos-formations?pole=digital-ia-bureautique",
  // Audit GSC mai 2026 — anciens slugs WP non couverts
  "excel-macros": "excel-vba-initiation",
  "anglais-30h-leveltel": "/nos-formations?pole=langues",
  "access-perfectionnement-2": "access-perfectionnement",
  "fle-en-cours-individuel-certification": "francais-langue-etrangere-fle",
  // Audit GSC round 3 — formations supprimées sans équivalent direct
  "sql-niveau-1": "/nos-formations",
  "la-comptabilite-appliquee-a-la-gestion-de-pme": "/nos-formations",
};

/** Static path redirects (no slug param) */
const STATIC_REDIRECTS: Record<string, string> = {
  "/catalogue": "/nos-formations",
  "/naxis-groupe": "/qui-sommes-nous",
  "/contact": "/nous-contacter",
  "/auth": "/mon-compte",
  "/formations-intra": "/",
  "/demarche-qualite": "/qui-sommes-nous#demarche-qualite",
  "/capskills": "/",
  "/accessibilite": "/qui-sommes-nous#accessibilite",
  "/profile/conseiller": "/qui-sommes-nous#equipe",
  "/grands-comptes": "/qui-sommes-nous#qui-sommes-nous",
  "/formations-certifiantes": "/certifications",
  "/lequipe-pedagogique": "/qui-sommes-nous#equipe",
  "/digital-learning": "/",
  "/centre-avon-fontainebleau": "/centre-fontainebleau",
  "/centre-bussy-st-georges": "/centre-bussy-saint-georges",
  "/inscription-2": "/mon-compte",
  "/centre-paris-boissiere": "/centre-paris",
  "/courses": "/nos-formations",
  // Audit GSC mai 2026 — chemins techniques WP
  "/images": "/",
  "/images/": "/",
  "/rating_over": "/",
};

/** Category redirects */
const CATEGORY_REDIRECTS: Record<string, string> = {
  "formations-certifiantes": "/certifications",
  "achats-logistique": "/nos-formations",
  "efficacite-professionnelle": "/nos-formations?pole=competences-transversales",
  "bureautique": "/nos-formations?pole=digital-ia-bureautique",
  "digital": "/nos-formations?pole=digital-ia-bureautique",
  "infographie-cao": "/nos-formations?pole=digital-ia-bureautique",
  "informatique": "/nos-formations?pole=digital-ia-bureautique",
  "hotellerie-restauration": "/nos-formations?pole=hotellerie-restauration",
  "gestion-de-projet": "/nos-formations?pole=management-rh",
  "management-de-la-qualite": "/nos-formations?pole=management-rh",
  "management-dequipe": "/nos-formations?pole=management-rh",
  "representants-du-personnel": "/nos-formations?pole=management-rh",
  "ressources-humaines": "/nos-formations?pole=management-rh",
  "developpement-commercial": "/nos-formations?pole=performance-commerciale",
  "prevention-securite": "/nos-formations?pole=securite-prevention",
  "langues": "/nos-formations?pole=langues",
  // Audit GSC mai 2026 — route active confirmée
  "partenariats-opco": "/partenariats-opco",
};

/** Redirect /course/:slug, /formation/:slug, /lesson/:slug, /formations/:slug */
export const LegacySlugRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const resolved = slug ? (SLUG_OVERRIDES[slug] || slug) : "";
  const target = resolved.startsWith("/") ? resolved : `/nos-formations/${resolved}`;
  trackRedirect(location.pathname, target);
  return <Navigate to={target} replace />;
};

/** Redirect /catalogue/:path */
export const LegacyCatalogueRedirect = () => {
  const { "*": rest } = useParams();
  const location = useLocation();
  const target = `/nos-formations/${rest || ""}`;
  trackRedirect(location.pathname, target);
  return <Navigate to={target} replace />;
};

/** Redirect /course-category/:cat */
export const LegacyCategoryRedirect = () => {
  const { cat } = useParams<{ cat: string }>();
  const location = useLocation();
  const dest = cat ? (CATEGORY_REDIRECTS[cat] || "/nos-formations") : "/nos-formations";
  trackRedirect(location.pathname, dest);
  return <Navigate to={dest} replace />;
};

/** Redirect /nos-formations/page/:num */
export const LegacyPaginationRedirect = () => {
  const location = useLocation();
  trackRedirect(location.pathname, "/nos-formations");
  return <Navigate to="/nos-formations" replace />;
};

/** Redirect /nos-formations/:slug with slug overrides */
export const LegacyFormationSlugRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  if (slug && SLUG_OVERRIDES[slug]) {
    const target = SLUG_OVERRIDES[slug].startsWith("/") ? SLUG_OVERRIDES[slug] : `/nos-formations/${SLUG_OVERRIDES[slug]}`;
    trackRedirect(location.pathname, target);
    return <Navigate to={target} replace />;
  }
  // Not an override — let the normal route handle it
  return null;
};

/** Static path redirect component */
export const LegacyStaticRedirect = ({ to }: { to: string }) => {
  const location = useLocation();
  trackRedirect(location.pathname, to);
  return <Navigate to={to} replace />;
};

/** Tracked inline redirect — drop-in replacement for <Navigate to=… replace /> */
export const TrackedRedirect = ({ to }: { to: string }) => {
  const location = useLocation();
  trackRedirect(location.pathname, to);
  return <Navigate to={to} replace />;
};

export { STATIC_REDIRECTS, SLUG_OVERRIDES };
